import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../components/coreUiTable/Table";
import LoadingAnimation from "../../../components/loadingAnimation/loadingAnimation";
import Filter from "./Filter";
import Switch from "./Switch";
import InfoTooltip from "./InfoTooltip";
import Overlay from "../../../components/globalOverlay/globalOverlay";
import ToggleableButton from "../../../components/toggleableButton/toggleableButton";

// returns array of indexes in form of [_1, _2, ..., _len]
const getColumns = (len) => {
  return [...Array(len - 2).keys()].map((key) => `_${key + 1}`);
};

const Heatmap = ({
  organizationId,
  locationId,
  objectId,
  regionId,
  includeCrossRegionalVisits,
  fetchHeatmapData,
  sourceType
}) => {
  const dispatch = useDispatch();
  const { isLoading, data, links, month, riders, filters } = useSelector((state) => state);
  const checked = filters?.numbersOrPercentages === "numbers";
  const [isActive, setIsActive] = useState(includeCrossRegionalVisits?.value ?? false);
  // fetching data
  useEffect(() => {
    dispatch(
      fetchHeatmapData({
        organizationId,
        locationId,
        objectId,
        regionId,
        includeCrossRegionalVisits,
        sourceType
      })
    );
  }, [organizationId, locationId, objectId, regionId]);

  // return modified td to inject into each cell
  const getTd = (item, index, col) => (
    <td
      style={{
        display: item < 0 ? "none" : "",
        backgroundColor:
          index > 1 ? (item >= 0 ? `rgba(41, 112, 172, ${item / 100})` : "#272a32") : ""
      }}
    >
      <div>
        {index > 0
          ? item >= 0 &&
            (index > 1 ? (
              <a
                target="_blank"
                href={links[index - 2][col - 1]}
                className="heatmap-link"
                rel="noreferrer"
              >
                {`${item}${checked ? "" : "%"}`}
              </a>
            ) : (
              `${item}${checked ? "" : "%"}`
            ))
          : item}
      </div>
    </td>
  );

  // returns object of objects to override each _n column in form of {_n: (item, index) => getTd}
  const getSlots = (firstRow) => {
    return Object.assign(
      {},
      ...firstRow.slice(2, firstRow.length).map((col) => {
        return { [`_${col}`]: (item, index) => getTd(item[`_${col}`], index, col) };
      })
    );
  };

  const update = ({
    numbersOrPercentages = filters.numbersOrPercentages,
    classPass = filters.classPass
  }) => {
    dispatch(
      fetchHeatmapData({
        organizationId,
        locationId,
        objectId,
        regionId,
        classPass: classPass,
        numbersOrPercentages: numbersOrPercentages,
        includeCrossRegionalVisits: { value: isActive }
      })
    );
  };

  const handleToggle = (isActive) => {
    setIsActive(isActive);
    dispatch(
      fetchHeatmapData({
        organizationId,
        locationId,
        objectId,
        regionId,
        classPass: filters.classPass,
        numbersOrPercentages: filters.numbersOrPercentages,
        includeCrossRegionalVisits: { value: isActive }
      })
    );
  };

  return data.length > 0 ? (
    <>
      <Overlay
        isVisible={isLoading}
        portalId="#react-heatmap-overlay"
        style={{ position: "absolute", height: "100%", width: "100%" }}
      >
        <LoadingAnimation />
      </Overlay>
      {/*categories can be missing*/}
      {filters && filters.categories && filters.categories.length > 0 && (
        <div
          className="heatmap-filters"
          style={{ padding: "0 0 1rem 0", width: "11%", display: "inline-flex" }}
        >
          <Filter
            keys={{ label: "label" }}
            options={filters?.categories}
            selectedFilters={filters?.categories.filter((category) => category.checked === true)}
            onChange={(values) => {
              dispatch(
                fetchHeatmapData({
                  organizationId,
                  locationId,
                  objectId,
                  regionId,
                  categories: values
                })
              );
            }}
            numberDisplayed={0}
            manySelectedPlaceholder="Pricing Options"
            allSelectedPlaceholder="Pricing Options"
          />
        </div>
      )}
      {filters && filters.classPasses && (
        <div
          className="heatmap-filters"
          style={{ padding: "0 0 1rem 0", width: "30%", display: "inline-flex" }}
        >
          <Switch
            checked={checked}
            onChange={(e) =>
              update({ numbersOrPercentages: e.target.checked ? "numbers" : "percentages" })
            }
          />
          <Filter
            isMulti={false}
            keys={{ label: "label", value: "value" }}
            options={filters?.classPasses}
            selectedFilters={filters.classPasses.find(({ value }) => value === filters?.classPass)}
            onChange={(classPass) => update({ classPass: classPass.value })}
          />
          <InfoTooltip
            tooltipId="class-pass-tooltip"
            style={{ color: "#337ab7", paddingTop: "1rem", marginLeft: "1rem" }}
            title={filters.tooltip}
          />
        </div>
      )}
      {includeCrossRegionalVisits && (
        <div
          className="heatmap-filters"
          style={{ display: "flex", flexDirection: "column", padding: "0 0 5rem 0" }}
        >
          <p style={{ color: "white" }}>Cross Regional Visits</p>
          <ToggleableButton
            isRadio
            onClick={(isActive) => handleToggle(isActive)}
            title=""
            isActive={isActive}
          />
        </div>
      )}
      <Table
        table={{
          colHeaders: ["month", "riders", ...getColumns(data[0].length)],
          columns: [],
          data
        }}
        overrideColumnCallback={(header, index) => ({
          label:
            index === 2 ? (
              <div className="month-info">
                <InfoTooltip
                  tooltipId="month-info-tooltip"
                  style={{ color: "#337ab7", paddingRight: "5px" }}
                  title={month.tooltip}
                />
                {month.text}
              </div>
            ) : (
              ""
            )
        })}
        scopedSlots={{
          month: (item) => <td className="month">{item.month}</td>,
          riders: (item, index) => (
            <td className="riders">
              {index === 0 && riders.text ? (
                <>
                  <div className="riders-label">{riders.text}</div>
                  <InfoTooltip
                    tooltipId="riders-tooltip"
                    style={{ color: "#337ab7" }}
                    title={riders.tooltip}
                  />
                </>
              ) : (
                item.riders
              )}
            </td>
          ),
          ...getSlots(data[0])
        }}
        tableFilter={false}
        pagination={false}
        tableWrapperId="heatmap"
      />
    </>
  ) : (
    <LoadingAnimation />
  );
};

Heatmap.propTypes = {
  locationId: PropTypes.number,
  objectId: PropTypes.number,
  regionId: PropTypes.number,
  organizationId: PropTypes.number,
  fetchHeatmapData: PropTypes.func.isRequired,
  sourceType: PropTypes.string
};

export default Heatmap;
